import React, { useEffect } from "react";
import { Check, X, Compass, Target, Megaphone, BarChart3, Cpu, ChevronLeft, ChevronRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";

const ICONS = { compass: Compass, target: Target, megaphone: Megaphone, chart: BarChart3, cpu: Cpu };

export const ServiceAreaDialog = ({ areas, index, labels, open, onOpenChange, onNavigate }) => {
  const total = Array.isArray(areas) ? areas.length : 0;
  const area = total && index !== null && index >= 0 ? areas[index] : null;
  const Icon = area ? ICONS[area.icon] || Compass : Compass;

  const canNavigate = total > 1 && typeof onNavigate === "function";

  useEffect(() => {
    if (!open || !canNavigate) return;
    const handler = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); onNavigate(1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); onNavigate(-1); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, canNavigate, onNavigate]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl border-white/10 bg-narwhal-panel text-white max-h-[85vh] overflow-y-auto" data-testid="model-area-dialog">
        {area && (
          <div>
            <div className="flex items-center gap-3 pr-8">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-narwhal-teal/20 bg-narwhal-teal/[0.08] text-narwhal-teal">
                <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
              </span>
              <DialogTitle className="text-lg font-semibold text-white md:text-xl" data-testid="model-area-dialog-title">{area.name}</DialogTitle>
            </div>

            <p className="mt-4 text-sm leading-relaxed text-narwhal-muted">
              <span className="font-semibold text-white/80">{labels.objetivo}. </span>{area.objetivo}
            </p>

            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-narwhal-teal">{labels.incluye}</p>
            <ul className="mt-3 grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
              {area.incluye.map((point, i) => (
                <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-narwhal-muted">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-narwhal-teal" strokeWidth={2.2} aria-hidden="true" />
                  <span>{point}</span>
                </li>
              ))}
            </ul>

            <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-white/40">{labels.noIncluye}</p>
            <div className="mt-3 flex items-start gap-3 text-sm leading-relaxed text-narwhal-muted">
              <X className="mt-0.5 h-4 w-4 shrink-0 text-white/40" strokeWidth={2.2} aria-hidden="true" />
              <span>{area.noIncluye}</span>
            </div>

            {area.note && (
              <div className="mt-6 rounded-xl border border-narwhal-teal/20 bg-narwhal-teal/[0.06] p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-narwhal-teal">{area.note.label}</p>
                <p className="mt-2 text-sm leading-relaxed text-white/80">{area.note.text}</p>
              </div>
            )}

            {canNavigate && (
              <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-5">
                <button
                  type="button"
                  onClick={() => onNavigate(-1)}
                  data-testid="model-area-dialog-prev"
                  className="group inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-narwhal-muted transition-colors hover:border-narwhal-teal/40 hover:text-white"
                >
                  <ChevronLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" aria-hidden="true" />
                  {labels.anterior || "Anterior"}
                </button>

                <div className="flex items-center gap-1.5" aria-hidden="true">
                  {areas.map((_, i) => (
                    <span
                      key={i}
                      className={`h-1.5 rounded-full transition-all ${i === index ? "w-5 bg-narwhal-teal" : "w-1.5 bg-white/20"}`}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  onClick={() => onNavigate(1)}
                  data-testid="model-area-dialog-next"
                  className="group inline-flex items-center gap-2 rounded-full border border-narwhal-teal/30 bg-narwhal-teal/10 px-4 py-2 text-sm font-medium text-narwhal-teal transition-colors hover:bg-narwhal-teal/20"
                >
                  {labels.siguiente || "Siguiente"}
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ServiceAreaDialog;
